/**
 * Sprint 24.5 — InstallStepConfirm (wizard step 1).
 *
 * Confirms what the user is about to connect. For OAuth templates it parses the
 * template's scopes into a scannable plain-English list (healthier than hiding
 * what's requested — Risks). For non-OAuth templates (the dev Custom MCP) it
 * shows the capability summary instead.
 */

import type { CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";

const SCOPE_LABELS: Record<string, string> = {
  "User.Read": "Read your basic profile",
  "Mail.Read": "Read your email",
  "Mail.ReadWrite": "Read and write your email",
  "Calendars.Read": "Read your calendar",
  "Calendars.ReadWrite": "Read and write your calendar",
  "Contacts.Read": "Read your contacts",
  "Files.Read.All": "Read your OneDrive files",
  "Chat.Read": "Read your Teams chats",
};

function humanizeScopes(scopes: string | undefined): string[] {
  if (!scopes) return [];
  return scopes
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => SCOPE_LABELS[s] ?? s);
}

export interface InstallStepConfirmProps {
  template: FirstPartyTemplate;
  onContinue: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function InstallStepConfirm({ template, onContinue, onCancel, busy }: InstallStepConfirmProps) {
  const scopes = humanizeScopes(template.connection_template.oauth_scopes);

  const avatar: CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: "var(--border-radius-md)",
    background: template.brand.avatar_bg,
    color: template.brand.avatar_color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    flexShrink: 0,
  };

  return (
    <div style={body}>
      <div style={headerRow}>
        <span style={avatar}>
          <i className={`ti ti-${template.brand.icon_name}`} aria-hidden="true" />
        </span>
        <h3 style={h3}>Connect {template.display_name}</h3>
      </div>

      {scopes.length > 0 ? (
        <>
          <div style={intro}>Kuvio will request access to:</div>
          <ul style={scopeList}>
            {scopes.map((s) => (
              <li key={s} style={scopeItem}>
                <i className="ti ti-check" style={scopeCheck} aria-hidden="true" />
                {s}
              </li>
            ))}
          </ul>
          <div style={subText}>You can disable specific toolsets after connecting.</div>
        </>
      ) : (
        <>
          <div style={intro}>{template.description}</div>
          <div style={subText}>{template.capability_footer}</div>
        </>
      )}

      <div style={footer}>
        <button type="button" style={cancelLink} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={primaryBtn} onClick={onContinue} disabled={busy}>
          {busy ? "Connecting…" : "Continue →"}
        </button>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const headerRow: CSSProperties = { display: "flex", alignItems: "center", gap: 10 };
const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };

const intro: CSSProperties = { fontSize: 13, color: "var(--color-text-secondary)" };

const scopeList: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const scopeItem: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "var(--color-text-primary)",
};

const scopeCheck: CSSProperties = { fontSize: 13, color: "var(--color-text-success)", flexShrink: 0 };

const subText: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5 };

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 4,
};

const cancelLink: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
